// ─── src/hooks/useChartLab.ts ─────────────────────────────────────────────────

import { useState, useCallback } from "react";
import type { ChartConfig, ChartSource, ChartType } from "../types/chartLab";

const STORAGE_KEY = "chart_lab_configs";

function load(): ChartConfig[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ChartConfig[]) : [];
  } catch {
    return [];
  }
}

function save(configs: ChartConfig[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
  } catch {
    // localStorage full or unavailable — silently skip
  }
}

export function useChartLab() {
  const [charts, setCharts] = useState<ChartConfig[]>(load);

  const addChart = useCallback(
    (source: ChartSource, metric: string, chartType: ChartType, title: string) => {
      const config: ChartConfig = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        source,
        metric,
        chartType,
        title,
      };
      setCharts((prev) => {
        const next = [...prev, config];
        save(next);
        return next;
      });
    },
    [],
  );

  const removeChart = useCallback((id: string) => {
    setCharts((prev) => {
      const next = prev.filter((c) => c.id !== id);
      save(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setCharts([]);
    save([]);
  }, []);

  return { charts, addChart, removeChart, clearAll };
}
